import { isOAuthError, isProblemDetail, messageOf } from './problem';
import type { FieldError, OAuthError, ProblemDetail } from './problem';

/**
 * Every failure a request can produce, as one type.
 *
 * <h2>Why a class rather than passing the axios error around</h2>
 *
 * An axios error makes each call site answer the same four questions before it
 * can do anything: did a response arrive at all, was it JSON, was it this
 * application's error shape or the OAuth one, and where is the sentence to show.
 * Answered once here, a caller reads `error.message` and `error.status` and is
 * done.
 *
 * <p>It also collapses the case that has no response — a dropped connection, a
 * request that timed out, a server that is not running. Those have no status and
 * no body, and a caller that forgets them shows "undefined" to a person.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly type: string;
  readonly traceId: string | undefined;
  readonly fieldErrors: FieldError[];
  readonly problem: ProblemDetail | undefined;

  /**
   * The problem type behind an RFC 6749 refusal, for the token endpoint only.
   *
   * <h2>Why this is not {@link type}</h2>
   *
   * For an OAuth refusal `type` is `oauth:` plus the RFC 6749 error code, which is
   * `oauth:invalid_grant` for a wrong password, an unknown address, an
   * unactivated account, a spent state and most other failures — vague on purpose,
   * so that signing in cannot be used to discover who has an account. Several
   * places already branch on that value and it must keep meaning what it means.
   *
   * <p>This is the narrower fact the server also sends, in `error_uri`: *which*
   * refusal it was, where it is willing to say. Undefined for every refusal that
   * deliberately says nothing, which is most of them.
   */
  readonly problemType: string | undefined;

  private constructor(init: {
    message: string;
    status: number;
    type: string;
    traceId?: string | undefined;
    fieldErrors?: FieldError[];
    problem?: ProblemDetail | undefined;
    problemType?: string | undefined;
  }) {
    super(init.message);
    this.name = 'ApiError';
    this.status = init.status;
    this.type = init.type;
    this.traceId = init.traceId;
    this.fieldErrors = init.fieldErrors ?? [];
    this.problem = init.problem;
    this.problemType = init.problemType;
  }

  /** An RFC 9457 problem from anywhere under `/api`. */
  static fromProblem(problem: ProblemDetail): ApiError {
    return new ApiError({
      message: messageOf(problem),
      status: problem.status,
      type: problem.type,
      traceId: problem.traceId,
      fieldErrors: problem.errors ?? [],
      problem,
    });
  }

  /**
   * An RFC 6749 error from the token endpoint.
   *
   * <p>Its wording is deliberately vague — `invalid_grant` is returned identically
   * for a wrong password and an unknown address, so that signing in cannot be used
   * to discover who has an account. Passing it through unchanged is correct;
   * improving it here would undo that.
   *
   * <p><b>One exception since 1 October 2026: a suspended account IS named.</b> The
   * server moved that check after the password comparison, so the message is
   * unreachable without the password and the caller has therefore already proved
   * the account is theirs. It arrives as a real sentence plus
   * `error_uri: https://xtremand.com/problems/account-suspended`, and this still
   * passes `error_description` through untouched — which is now the right thing for
   * a different reason. Before the move, a suspended person was shown the sentence a
   * mistyped password gets, reset their password successfully, and was refused
   * again.
   *
   * <p><b>Not every `{ error }` body is an OAuth one.</b> A few endpoints, the brand analysis among them, answer a failure as
   * `{ "error": "Your API key has expired.", "status": 401 }`: the sentence itself in `error`, with no `error_description`.
   * An RFC 6749 code is a token with no spaces (`invalid_grant`), so an `error` that contains a space is a sentence written
   * for a person, and it is what the person is shown. Without this, such a reply fell to the vague sign-in message below and
   * somebody refused because their API key had expired was told "Sign-in failed."
   */
  static fromOAuth(error: OAuthError, status: number): ApiError {
    const isSentence = /\s/.test(error.error);
    return new ApiError({
      message: error.error_description ?? (isSentence ? error.error : 'Sign-in failed.'),
      status,
      type: isSentence ? 'legacy-error' : `oauth:${error.error}`,
      // Carried since 16 October 2026 and discarded before it. `error_description`
      // still passes through untouched, so nothing that reads the sentence changes.
      problemType: error.error_uri,
    });
  }

  /**
   * A response arrived, but from something in front of the application rather than
   * from the application.
   *
   * <h2>How this is told apart from a real server error</h2>
   *
   * Every failure this API raises answers `application/problem+json`. A 5xx that does
   * not is therefore not ours: it came from a proxy, a load balancer or a gateway
   * that could not reach us. In development that is Vite's dev proxy, which answers
   * `500 text/plain` when nothing is listening on 8080.
   *
   * <h2>Why it needed its own case</h2>
   *
   * Without it this fell to {@link fromUnexpectedResponse}, whose message is
   * "Something went wrong. Please try again." That is wrong twice over: it blames
   * the application for a fault the application never saw, and "try again" invites
   * somebody to retry immediately against a server that is down.
   *
   * <p>It is also distinct from {@link fromNetwork}. That one means no response at
   * all — offline, or a timeout — where "check your connection" is the right advice.
   * Here a response DID arrive, so the connection is fine and the advice would send
   * somebody to look at the wrong thing.
   *
   * <h2>No traceId, and that is the tell</h2>
   *
   * Every real failure carries one. Its absence is the honest signal that the
   * request never reached anything that could have logged it.
   */
  static fromUnreachable(status: number): ApiError {
    return new ApiError({
      /*
       * Two things a person needs, in the order they need them.
       *
       * It says nothing about a server "restarting", which was a guess about
       * internals nobody outside this building can act on and is usually wrong --
       * the process may be stopped, redeploying, or gone.
       *
       * And it says the details were fine, because that is the FIRST thing
       * somebody assumes when a sign-in fails: that they mistyped. They did not,
       * and nothing here even looked -- so leaving them to check a password that
       * was never examined is the actual cost of the old wording.
       *
       * <p>THIS SENTENCE IS RAISED ON EVERY ENDPOINT, which is why it stays
       * general. The sign-in screen says something of its own for the same
       * condition -- see `SignInPage` -- because "your details" means nothing on a
       * screen that has no credentials on it.
       */
      message:
        'We can’t reach Xtremand right now — nothing was wrong with your details. Try again in a moment.',
      status,
      type: 'unreachable',
    });
  }

  /** A response arrived, but not in a shape this application defines. */
  static fromUnexpectedResponse(status: number): ApiError {
    return new ApiError({
      message: 'Something went wrong. Please try again.',
      status,
      type: 'about:blank',
    });
  }

  /**
   * The session ended and could not be renewed.
   *
   * <p>Distinct from a plain 401 so a screen can tell "you were signed out" from
   * "you are not allowed to do that" -- they look identical on the wire and mean
   * quite different things to a person.
   */
  static fromSessionExpired(): ApiError {
    return new ApiError({
      message: 'Your session has ended. Please sign in again.',
      status: 401,
      type: 'session-expired',
    });
  }

  /** No response at all: offline, timed out, or nothing listening. */
  static fromNetwork(): ApiError {
    return new ApiError({
      /*
       * No reassurance about the details here, deliberately: with no response at
       * all the cause is as likely to be this machine as ours, and "nothing was
       * wrong with your details" would be a claim made without evidence. What is
       * actionable is the connection.
       */
      message: 'We can’t reach Xtremand. Check your connection and try again.',
      status: 0,
      type: 'network',
    });
  }

  /**
   * Whether this failure carries that problem type, wherever the server put it.
   *
   * <h2>Why a method rather than comparing a field</h2>
   *
   * The same URI reaches this object through two different fields depending on
   * which endpoint answered: {@link type} for an RFC 9457 problem document from
   * anywhere under `/api`, and {@link problemType} for an RFC 6749 refusal from
   * `/oauth2/token`, where it rides in `error_uri` because RFC 6749 §5.2 fixes the
   * set of `error` codes a token endpoint may use.
   *
   * <p>A caller that compared only one of them would work on one endpoint and
   * silently fail on the other — and silently is the problem: the fallback is the
   * server's own sentence, so a missed match looks like nothing happening rather
   * than like a bug. `social-provider-not-available` arrives both ways, which is
   * what made this worth having rather than two comparisons at each call site.
   *
   * <p>**Never a status code.** Several problem types share a status, and several
   * statuses carry no type at all.
   *
   * @param problemType one of {@link PROBLEM_TYPES}
   */
  carries(problemType: string): boolean {
    return this.type === problemType || this.problemType === problemType;
  }

  /** Whether the caller is unauthenticated, as opposed to merely not permitted. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  /** Whether this is worth retrying rather than reporting. */
  get isRetryable(): boolean {
    return this.status === 0 || this.status >= 500;
  }
}

export { isOAuthError, isProblemDetail };
